import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { LoadingOutlined } from '@ant-design/icons';
import { MutationActionCreatorResult, MutationDefinition } from '@reduxjs/toolkit/query';

import { Spin } from 'antd';
import { setDrawerCloseDisabled } from 'api/client-side-slices/DesignSlice';
import { useAppDispatch } from 'app-redux/hooks';
import { SearchInput } from 'components';
import dayjs from 'dayjs';
import { GetEntityModel, GetPersonModel } from 'solstice-types';
import { useDebounce } from 'use-debounce';
import { UserModel } from 'utils';
import { SelectedIndividual } from './components';

interface AddNewIndividualContentProps {
  searchIndividual: (
    params: any
  ) => MutationActionCreatorResult<MutationDefinition<any, any, any, GetPersonModel[], 'primeApi'>>;
  disabled?: boolean;
  data?: GetPersonModel[];
  isLoading?: boolean;
  removeIndividual?: (index: number) => void;
}

export const AddNewIndividualContent = ({
  disabled,
  searchIndividual,
  data = [],
  isLoading,
  removeIndividual,
}: AddNewIndividualContentProps) => {
  const [selectedIndividual, setSelectedIndividual] = useState<any | null>(null);
  const [areInputsVisible, setAreInputsVisible] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isExistingVisible, setIsExistingVisible] = useState<boolean>(false);
  const [isSearchComplete, setIsSearchComplete] = useState<boolean>(false);

  const { control, getValues, setValue, reset } = useFormContext();

  const dispatch = useAppDispatch();
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (!debouncedSearchQuery) {
      setIsSearchComplete(false);
      return;
    }

    const fetchData = async () => {
      setIsSearchComplete(false);
      try {
        await searchIndividual({ searchTerm: debouncedSearchQuery });
        setIsSearchComplete(true);
      } catch {
        setIsSearchComplete(true);
      }
    };

    fetchData();
  }, [debouncedSearchQuery, searchIndividual]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (!value.trim()) {
      setIsSearchComplete(false);
    }
  };

  // const handleAddAddress = (value: any) => {
  //   setSelectedIndividual({ ...selectedIndividual, address: value });
  // };

  const searchOptions =
    isSearchComplete && searchQuery && data.length === 0
      ? [
          {
            value: 'no-result',
            label: <div className="text-gray-500 font-medium">No results found.</div>,
          },
        ]
      : searchQuery
        ? data.map((user: UserModel) => ({
            value: user.id,
            label: <SelectedIndividual user={user} searchQuery={searchQuery} areOptionsVisible />,
          }))
        : [];

  const handleSelect = (value: string) => {
    if (value === 'no-result') return;

    setIsExistingVisible(true);
    setAreInputsVisible(false);
    dispatch(setDrawerCloseDisabled(false));

    const selectedItem = data.find((item: GetEntityModel) => item.id === value);
    const { firstName, lastName, mobilePhone, email, dateOfBirth, middleName, address } =
      selectedItem ?? {};

    if (selectedItem) {
      setSelectedIndividual(selectedItem);
      setSearchQuery('');

      setValue('firstName', firstName);
      setValue('lastName', lastName);
      setValue('mobilePhone', mobilePhone);
      setValue('email', email);
      setValue('middleName', middleName);
      setValue('address', address);

      const parsedDate = dayjs(dateOfBirth);

      const isDefaultInvalidDate =
        parsedDate.isValid() && parsedDate.format('DD/MM/YYYY') === '01/01/1901';

      if (parsedDate.isValid() && !isDefaultInvalidDate) {
        setValue('dateOfBirth', parsedDate);
      }
    }
  };

  const handleSelectAddress = (value: any) => {
    setSelectedIndividual({ ...selectedIndividual, ...value });
  };

  const handleRemoveIndividual = (index: number) => {
    const currentIndividual = getValues('individuals') || [];
    const updatedIndividual = currentIndividual.filter((_: any, i: number) => i !== index);
    setValue('individuals', updatedIndividual);

    const updatedFields = Object.entries(getValues()).reduce<Record<string, any>>(
      (acc, [key, value]) => {
        if (key.includes(`_${100}`)) {
          return acc;
        }

        acc[key] = value;
        return acc;
      },
      {}
    );

    reset(updatedFields);
    removeIndividual?.(index);
    reset();

    setAreInputsVisible(false);
    setSearchQuery('');
    setSelectedIndividual(null);
    setIsExistingVisible(false);
    dispatch(setDrawerCloseDisabled(false));
  };
  const handleRemoveSelected = () => {
    if (!selectedIndividual) setAreInputsVisible(!areInputsVisible);
    reset();
    setSearchQuery('');
    setSelectedIndividual(null);
    setIsExistingVisible(false);
    dispatch(setDrawerCloseDisabled(true));
  };

  return (
    <>
      <span className="text-gray-600">
        Search for existing individual from previous KYC verifications.
      </span>
      <SearchInput
        placeholder="Type to search..."
        onSearch={handleSearch}
        onSelect={handleSelect}
        value={searchQuery}
        options={searchOptions}
        disabled={areInputsVisible || disabled}
        suffixIcon={
          isLoading ? (
            <Spin className="mt-2" size="default" indicator={<LoadingOutlined spin />} />
          ) : (
            ''
          )
        }
      />

      {selectedIndividual && (
        <SelectedIndividual
          user={selectedIndividual}
          searchQuery={searchQuery}
          isExistingVisible={isExistingVisible}
          handleDisplayInputs={handleRemoveSelected}
          control={control}
          handleAddress={handleSelectAddress}
        />
      )}
    </>
  );
};
